<template>
  <div v-if="workspaceOptions.length" class="workspace-switcher" :class="{ 'workspace-switcher--compact': compact }">
    <button
      v-if="compact"
      type="button"
      class="space-chip"
      :style="{ borderColor: currentAccent }"
      aria-label="Cambiar espacio"
      :aria-expanded="open"
      @click="openSheet"
    >
      <VaIcon :name="currentIcon" size="18px" :color="currentAccent" />
      <span class="space-chip__name">{{ shortName }}</span>
      <VaIcon name="expand_more" size="18px" color="#0b193d" />
    </button>

    <VaSelect
      v-else
      v-model="workspace"
      :options="workspaceOptions"
      text-by="name"
      value-by="id"
      aria-label="Proyecto activo"
      class="workspace-select"
    />

    <Teleport to="body">
      <div v-if="compact && open" class="space-sheet">
        <button type="button" class="space-sheet__backdrop" aria-label="Cerrar" @click="open = false" />
        <div class="space-sheet__panel" role="dialog" aria-modal="true" aria-label="Espacios">
          <div class="space-sheet__handle" />
          <h2 class="space-sheet__title">Espacios</h2>
          <button
            v-for="item in workspaceOptions"
            :key="item.id"
            type="button"
            class="space-sheet__row"
            :class="{ 'space-sheet__row--active': item.id === activeId }"
            :style="{ '--space-accent': workspaceAccent(item.id, item.type) }"
            @click="selectSpace(item)"
          >
            <span class="space-sheet__icon">
              <VaIcon :name="item.icon" size="22px" :color="workspaceAccent(item.id, item.type)" />
            </span>
            <span class="space-sheet__name">{{ item.name }}</span>
            <span v-if="balances[item.id] != null" class="space-sheet__balance">{{ formatMoney(balances[item.id]) }}</span>
            <VaIcon v-if="item.id === activeId" name="check" size="20px" :color="workspaceAccent(item.id, item.type)" />
          </button>
          <button type="button" class="space-sheet__create" @click="createSpace">
            <VaIcon name="add" size="22px" color="#0b193d" />
            Nuevo proyecto
          </button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'vuestic-ui'
import { useUserStore } from '../../../stores/user'
import { useRequesterStore } from '../../../stores/requester'
import { formatMoney } from '../../../pages/accounts/accountUtils'
import { workspaceAccent } from '../workspaceAccent'

const props = defineProps({
  compact: { type: Boolean, default: false },
})

const userStore = useUserStore()
const router = useRouter()
const requester = useRequesterStore()
const { init: notify } = useToast()
const open = ref(false)
const balances = ref<Record<string, number>>({})
const toastKey = 'chullaWorkspaceSwitchToast'

function workspaceIcon(type?: string) {
  return String(type || '').toUpperCase() === 'PERSONAL' ? 'account_balance_wallet' : 'business'
}

const workspaceOptions = computed(() =>
  userStore.workspaces.map((item) => ({
    ...item,
    icon: workspaceIcon(item.type),
  })),
)

const activeId = computed(() => String(userStore.activeWorkspace ?? ''))

const current = computed(() => workspaceOptions.value.find((item) => item.id === activeId.value) ?? null)

const currentAccent = computed(() => (current.value ? workspaceAccent(current.value.id, current.value.type) : '#2f9e44'))

const currentIcon = computed(() => current.value?.icon || 'account_balance_wallet')

const shortName = computed(() => {
  if (!current.value) return ''
  if (String(current.value.type).toUpperCase() === 'PERSONAL') return 'Personal'
  return current.value.name
})

const workspace = computed<string | null>({
  get: () => userStore.activeWorkspace,
  set: (id) => {
    if (!id) return
    switchTo(String(id))
  },
})

function switchTo(id: string) {
  if (id === activeId.value) return
  const next = workspaceOptions.value.find((item) => item.id === id)
  sessionStorage.setItem(toastKey, next?.name || 'este espacio')
  userStore.setActiveWorkspace(id)
  window.location.reload()
}

function selectSpace(item: { id: string }) {
  open.value = false
  if (item.id === activeId.value) return
  switchTo(item.id)
}

function createSpace() {
  open.value = false
  router.push({ name: 'workspace' })
}

function openSheet() {
  open.value = true
}

async function loadBalances() {
  await Promise.all(
    userStore.workspaces.map(async (item) => {
      try {
        const response = await requester.Get({
          route: '/account',
          withAuth: true,
          extraHeader: { 'x-workspace-id': item.id },
        })
        const list = Array.isArray(response.data) ? response.data : []
        balances.value[item.id] = list
          .filter((account) => account?.active !== false)
          .reduce((sum, account) => sum + Number(account.balance || 0), 0)
      } catch {
        delete balances.value[item.id]
      }
    }),
  )
}

watch(open, (value) => {
  document.body.style.overflow = value ? 'hidden' : ''
  if (value) loadBalances()
})

onMounted(() => {
  if (!props.compact) return
  const name = sessionStorage.getItem(toastKey)
  if (!name) return
  sessionStorage.removeItem(toastKey)
  notify({ message: `Cambiaste a ${name}`, color: 'success' })
})

onUnmounted(() => {
  document.body.style.overflow = ''
})
</script>

<style scoped>
.workspace-select {
  width: min(190px, 32vw);
  --va-input-wrapper-background: white;
}

.workspace-switcher--compact {
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  max-width: none;
}

.space-chip {
  display: flex;
  align-items: center;
  gap: 0.15rem;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  height: 32px;
  padding: 0 0.3rem 0 0.45rem;
  border: 2px solid;
  border-radius: 999px;
  background: #fff;
  color: #0b193d;
  cursor: pointer;
}

.space-chip__name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1;
}

.workspace-select :deep(.va-select-content .va-icon) {
  color: #0b193d;
}
</style>

<style>
.space-sheet {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: flex;
  align-items: flex-end;
}

.space-sheet__backdrop {
  position: absolute;
  inset: 0;
  border: 0;
  background: rgba(11, 25, 61, 0.45);
}

.space-sheet__panel {
  position: relative;
  width: 100%;
  max-height: min(78vh, 560px);
  overflow: auto;
  padding: 0.55rem 0.85rem calc(0.85rem + env(safe-area-inset-bottom));
  border-radius: 1.1rem 1.1rem 0 0;
  background: #fff;
  box-shadow: 0 -8px 28px rgba(11, 25, 61, 0.18);
}

.space-sheet__handle {
  width: 2.5rem;
  height: 0.28rem;
  margin: 0.15rem auto 0.75rem;
  border-radius: 999px;
  background: rgba(11, 25, 61, 0.2);
}

.space-sheet__title {
  margin: 0 0 0.45rem;
  font-size: 1rem;
  font-weight: 700;
  color: #0b193d;
}

.space-sheet__row,
.space-sheet__create {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  min-height: 48px;
  padding: 0.55rem 0.35rem;
  border: 0;
  border-radius: 0.75rem;
  background: transparent;
  color: #0b193d;
  text-align: left;
  cursor: pointer;
}

.space-sheet__row--active {
  background: color-mix(in srgb, var(--space-accent) 14%, #fff);
}

.space-sheet__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  flex: 0 0 auto;
  border-radius: 999px;
  background: color-mix(in srgb, var(--space-accent) 16%, #fff);
}

.space-sheet__name {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.95rem;
  font-weight: 600;
}

.space-sheet__balance {
  flex: 0 0 auto;
  font-size: 0.85rem;
  font-weight: 700;
  color: #0b193d;
}

.space-sheet__create {
  margin-top: 0.35rem;
  border-top: 1px solid rgba(11, 25, 61, 0.12);
  border-radius: 0;
  font-weight: 700;
}
</style>
